// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import _ from "lodash";
import React, { RefObject } from "react";
import { connect } from "react-redux";
import { RouteComponentProps, Link } from "react-router-dom";
import BeforeUnloadComponent from "react-beforeunload-component";
import axios from "axios";
import size from "lodash/size";
import SplitPane from "react-split-pane";
import { toast } from "react-toastify";
import { bindActionCreators } from "redux";
//import { PrimaryButton } from "office-ui-fabric-react";

import { PrimaryButton } from "@fluentui/react";
import HtmlFileReader from "../../../../common/htmlFileReader";
import { strings } from "../../../../common/strings";
import {
    AssetState, AssetType, EditorMode, IApplicationState,
    IAppSettings, IAsset, IAssetMetadata, IProject, IRegion,
    ISize, ITag, ITableColumnName, ITableRowName, FieldType, FieldFormat,
    ILabel, ITableTag, IOcrContent, IAuthenticationState, IUploadFileStatus,
} from "../../../../models/applicationState";
import { getNextColor } from "../../../../common/utils";
import IApplicationActions, * as applicationActions from "../../../../redux/actions/applicationActions";
import IProjectActions, * as projectActions from "../../../../redux/actions/projectActions";
import IUserOperationsActions, * as userOperationsActions from "../../../../redux/actions/operationsActions";
import IFilesUploadingAction, * as filesUploadingAction from "../../../../redux/actions/filesUploadingAction";
import IAppTitleActions, * as appTitleActions from "../../../../redux/actions/appTitleActions";
import { deleteFileEndPoint, accessToken, refreshTokenEndPoint, refreshTokenCall } from '../../../../common/api-config'
import ServiceHelper from "../../../../services/serviceHelper";
import { AssetPreview } from "../../common/assetPreview/assetPreview";
import { KeyboardBinding } from "../../common/keyboardBinding/keyboardBinding";
import { KeyEventType } from "../../common/keyboardManager/keyboardManager";
import { TagInput } from "../../common/tagInput/tagInput";
import { tagIndexKeys } from "../../common/tagInput/tagIndexKeys";
import Canvas from "./canvas";
import CanvasHelpers from "./canvasHelpers";
import "./editorPage.scss";
import EditorSideBar from "./editorSideBar";
import Alert from "../../common/alert/alert";
import Confirm from "../../common/confirm/confirm";
import { OCRService } from "../../../../services/ocrService";
import { throttle } from "../../../../common/utils";
import { constants } from "../../../../common/constants";
import PreventLeaving from "../../common/preventLeaving/preventLeaving";
import { Spinner, SpinnerSize } from "office-ui-fabric-react/lib/Spinner";
import { getPrimaryGreenTheme, getPrimaryRedTheme } from "../../../../common/themes";
import { SkipButton } from "../../shell/skipButton";
import ModalHeader from "../header/subHeader";
import HeaderComponent from "../header/header";
import LoadingOverlay from "react-loading-overlay";

/**
 * Properties for Editor Page
 * @member project - Project being edited
 * @member recentProjects - Array of projects recently viewed/edited
 * @member actions - Project actions
 * @member applicationActions - Application setting actions
 */
export interface IEditorPageProps extends RouteComponentProps, React.Props<EditorPage> {
    project: IProject;
    recentProjects: IProject[];
    generatedOcrContentsData: IOcrContent[];
    appSettings: IAppSettings;
    actions: IProjectActions;
    loggedInUser: IAuthenticationState;
    userOperationsActions: IUserOperationsActions;
    applicationActions: IApplicationActions;
    appTitleActions: IAppTitleActions;
    filesUploadingAction: IFilesUploadingAction;
    loadAnnotateFiles: IAsset[];
    uploadFilesCount?: number;
}

/**
 * State for Editor Page
 */
export interface IEditorPageState {
    /** Array of assets in project */
    assets: IAsset[];
    /** The editor mode to set for canvas tools */
    editorMode: EditorMode;
    /** The selected asset for the primary editing experience */
    selectedAsset?: IAssetMetadata;
    /** Currently selected region on current asset */
    selectedRegions?: IRegion[];
    /** Most recently selected tag */
    selectedTag: string;
    /** Tags locked for region labeling */
    lockedTags: string[];
    /** Size of the asset thumbnails to display in the side bar */
    thumbnailSize: ISize;
    /**
     * Whether or not the editor is in a valid state
     * State is invalid when a region has not been tagged
     */
    isValid: boolean;
    /** Whether the show invalid region warning alert should display */
    showInvalidRegionWarning: boolean;
    /** Show tags when loaded */
    tagsLoaded: boolean;
    /** The currently hovered TagInputItemLabel */
    hoveredLabel: ILabel;
    /** Whether the task for loading all OCRs is running */
    isRunningOCRs?: boolean;
    /** Whether OCR is running in the main canvas */
    isCanvasRunningOCR?: boolean;
    isError?: boolean;
    errorTitle?: string;
    tableName: string;
    showTableInput: boolean;
    errorMessage?: string;
    tableColumns: any[];
    columnName: ITag;
    columnNames: ITag[];
    tableRows: ITableTag[];
    tableData: any[];
    tableBindedColumns: any[];
    tableBindedRows: any[];
    labelValuesAsTable: any[];
    tags: ITag[];
    ocrFilesList: any[];
    labelFilesList: any[];
    selectedOcrFile: any;
    listOfBlobFiles: any;
    showLoaderForFiles: boolean;
    allFilesAreLoaded: boolean;
    userId: string;
    uploadFilesCount: number;
    showConfirmPopUpMessage: boolean,
}

function isNameEqual(x: string, y: string) {
    return x.trim().toLocaleLowerCase() === y.trim().toLocaleLowerCase();
}

function mapStateToProps(state: IApplicationState) {
    return {
        recentProjects: state.recentProjects,
        generatedOcrContentsData: state.selectedOcrFiles,
        project: state.currentProject,
        appSettings: state.appSettings,
        loggedInUser: state.loggedInUser,
        loadAnnotateFiles: state.loadAllFiles,
        uploadFilesCount: state.uploadFilesCount,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        filesUploadingAction: bindActionCreators(filesUploadingAction, dispatch),
        actions: bindActionCreators(projectActions, dispatch),
        userOperationsActions: bindActionCreators(userOperationsActions, dispatch),
        applicationActions: bindActionCreators(applicationActions, dispatch),
        appTitleActions: bindActionCreators(appTitleActions, dispatch),
    };
}

/**
 * @name - Editor Page
 * @description - Page for adding/editing/removing tags to assets
 */
@connect(mapStateToProps, mapDispatchToProps)

export default class EditorPage extends React.Component<IEditorPageProps, IEditorPageState> {
    public state: IEditorPageState = {
        userId: null,
        selectedTag: null,
        lockedTags: [],
        tableRows: [],
        tableColumns: [],
        tableName: null,
        tags: [],
        columnName: null,
        columnNames: [],
        assets: [],
        labelValuesAsTable: [],
        showTableInput: true,
        tableBindedColumns: [],
        tableBindedRows: [],
        tableData: [],
        labelFilesList: [],
        ocrFilesList: [],
        editorMode: EditorMode.Select,
        thumbnailSize: { width: 175, height: 155 },
        isValid: true,
        showInvalidRegionWarning: false,
        tagsLoaded: false,
        hoveredLabel: null,
        selectedOcrFile: null,
        listOfBlobFiles: [],
        showLoaderForFiles: false,
        allFilesAreLoaded: false,
        uploadFilesCount: null,
        showConfirmPopUpMessage: false,
    };

    private tagInputRef: RefObject<TagInput>;

    private loadingProjectAssets: boolean = false;
    private timeInterval: any;
    private canvas: RefObject<Canvas> = React.createRef();
    private renameTagConfirm: React.RefObject<Confirm> = React.createRef();
    private deleteTagValueConfirm: React.RefObject<Confirm> = React.createRef();
    private deleteFileConfirm: React.RefObject<Confirm> = React.createRef();
    private renameCanceled: () => void;
    private deleteTagConfirm: React.RefObject<Confirm> = React.createRef();
    private deleteTagOrTagValueConfirm: React.RefObject<Confirm> = React.createRef();
    private showConfirmPop: React.RefObject<Confirm> = React.createRef();
    private isUnmount: boolean = false;

    constructor(props) {
        super(props);
        this.tagInputRef = React.createRef();
    }

    public async componentDidMount() {
        window.addEventListener("focus", this.onFocused);
        // window.addEventListener('beforeunload', (event) => {
        //     event.returnValue = `Are you sure you want to leave?`;
        //   });
        this.isUnmount = false;
        const projectId = this.props.match.params["projectId"];
        const projectName = this.props.match.params["projectName"];
        const user = this.props.loggedInUser;
        const userId = user.user.oid;
        this.setState({ userId });
        if (this.props.project) {
            this.loadProjectAssets();
        } else if (projectId) {
            const recentProject = await this.props.recentProjects.find((project) => project.id === projectId);
            const projectCopy = await Object.assign({}, recentProject);
            projectCopy.folderPath = `${projectName}-${userId}/training`;
            projectCopy.tags = projectCopy.tags;
            await this.props.actions.saveProject(projectCopy, false, false);
            await this.props.actions.loadProject(projectCopy);
            await this.props.actions.updateProjectTagsFromFiles(this.props.project);
            this.setState({ tagsLoaded: true });
        }
        this.getFilesFromBlob();
    }

    public getFilesFromBlob = () => {
        const projectCopy = Object.assign({}, this.props.project);
        projectCopy.folderPath = `${this.props.match.params["projectName"]}-${this.state.userId}/training`;
        this.props.actions.loadBlobs(projectCopy, "training");
    }

    public async componentDidUpdate(prevProps: Readonly<IEditorPageProps>, prevState) {
        const { project } = this.props;
        if (project && this.state.assets.length === 0) {
            await this.loadProjectAssets();
        }

        if (project && prevProps.project && project.tags !== prevProps.project.tags) {
            this.updateRootAssets();
        }
        if (prevProps.uploadFilesCount && prevProps.uploadFilesCount !== prevState.uploadFilesCount) {
            prevState.uploadFilesCount = prevProps.uploadFilesCount;
            this.getFilesFromBlob();
        }
        if (this.props.loadAnnotateFiles !== prevProps.loadAnnotateFiles) {
            await this.loadProjectAssets();
        } else {
            localStorage.setItem("filesLengthFromModel", prevProps.loadAnnotateFiles.length.toString())
        }
    }

    public componentWillUnmount() {
        this.isUnmount = true;
        clearInterval(this.timeInterval);
        window.removeEventListener("focus", this.onFocused);
    }

    public render() {
        const { project } = this.props;
        const { assets, selectedAsset, isRunningOCRs, isCanvasRunningOCR,
            columnNames, selectedOcrFile} = this.state;
        const labels = (selectedAsset &&
            selectedAsset.labelData &&
            selectedAsset.labelData.labels) || [];
        const showTable = size(columnNames) > 0 ? true : false;
        const routeUrl = `/upload-files/${this.props.match.params["projectName"]}/${this.props.match.params["schema"]}/${this.props.match.params["projectId"]}`;
        const needRunOCRButton = assets.some((asset) => asset.state === AssetState.NotVisited);
        if (!project) {
            return (<div className="text-center mt-5">Loading...</div>);
        }
        return (
            <>
             <HeaderComponent
                history={this.props.history}
                match={this.props.match}
                location={this.props.location} />
                <ModalHeader
                    history={this.props.history}
                    match={this.props.match}
                    actions={this.props.actions}
                    location={this.props.location}
                    project={this.props.project} />
                <div className="container-fluid animated fadeIn editor-page" id="pageEditor">                    
                <BeforeUnloadComponent
                        alertMessage='Are you sure you want to leave? Changes will not be saved.'
                    >
                    {
                        tagIndexKeys.map((index) =>
                            (<KeyboardBinding
                                displayName={strings.editorPage.tags.hotKey.apply}
                                key={index}
                                keyEventType={KeyEventType.KeyDown}
                                accelerators={[`${index}`]}
                                icon={"fa-tag"}
                                handler={this.handleTagHotKey} />))
                    }
                    <SplitPane split="vertical"
                        defaultSize={this.state.thumbnailSize.width}
                        minSize={175}
                        maxSize={175}
                        paneStyle={{ display: "flex" }}
                        onChange={this.onSideBarResize}
                        onDragFinished={this.onSideBarResizeComplete}>
                        <div className="editor-page-sidebar bg-lighter-1">                           
                            <>
                            <table cellSpacing="0" cellPadding="0">
                                <tbody>
                                <tr>
                                    <td className="text-center" >
                                        <Link to={routeUrl}
                                            type="button" className="ul-more">
                                            <i className="fa fa fa-upload" aria-hidden="true"></i> Upload
                                </Link>
                                    </td>
                                    {needRunOCRButton && <td className="text-center" >
                                        <PrimaryButton
                                            theme={getPrimaryGreenTheme()}
                                            className="run-all"
                                            type="button"
                                            onClick={this.loadAllOCRs}
                                            disabled={this.state.isRunningOCRs}>
                                            {this.state.isRunningOCRs ?
                                                <div>
                                                    <Spinner
                                                        size={SpinnerSize.small}
                                                        label="Running OCR"
                                                        ariaLive="off"
                                                        labelPosition="right"
                                                    />
                                                </div> : "Run OCR on all"
                                            }
                                        </PrimaryButton>

                                    </td>
                                    }
                                </tr>
                                </tbody>
                            </table>
                            </>
                            {size(assets) !== 0 ?

                            
                            <EditorSideBar
                                assets={assets}
                                selectedAsset={selectedAsset ? selectedAsset.asset : null}
                                onBeforeAssetSelected={this.onBeforeAssetSelected}
                                onDeleteSelected={(e: IAsset) => this.deleteAsset(e)}
                                onAssetSelected={(e: IAsset) => this.selectAsset(e)}
                                thumbnailSize={this.state.thumbnailSize}
                                setImageUriInAsset={this.props.actions.updateAssetWithImageUri}
                            />
                                : <Spinner
                                size={SpinnerSize.small}
                                label="Loading Files"
                                ariaLive="off"
                                labelPosition="bottom"
                            />
                           }
                        </div>
                        <div className="editor-page-content" onClick={this.onPageClick}>
                            <div className="editor-page-content-main parant-table-annotate" >
                            {selectedAsset ?
                                <div className="editor-page-content-main-body" onClick={this.onPageContainerClick}>
                                        <Canvas
                                            ref={this.canvas}
                                            selectedAsset={this.state.selectedAsset}
                                            onAssetMetadataChanged={this.onAssetMetadataChanged}
                                            onCanvasRendered={this.onCanvasRendered}
                                            onSelectedRegionsChanged={this.onSelectedRegionsChanged}
                                            onRunningOCRStatusChanged={this.onCanvasRunningOCRStatusChanged}
                                            onTagChanged={this.onTagChanged}
                                            editorMode={this.state.editorMode}
                                            project={this.props.project}
                                            lockedTags={this.state.lockedTags}
                                            selectedOcrFile={selectedOcrFile}
                                            getOcrBasedOnOcrContent={this.getCanvasOcr}
                                            hoveredLabel={this.state.hoveredLabel}>
                                            <AssetPreview
                                                controlsEnabled={this.state.isValid}
                                                onBeforeAssetChanged={this.onBeforeAssetSelected}
                                                asset={this.state.selectedAsset.asset} />
                                        </Canvas>

                                </div>
                                : <Spinner
                                    size={SpinnerSize.large}
                                    label="Loading File To Annotate"
                                    ariaLive="off"
                                    className="mt-5"
                                    labelPosition="bottom"
                                />
                                    }                              
                            </div>

                            <div className="editor-page-right-sidebar">
                                <TagInput
                                    tagsLoaded={this.state.tagsLoaded}
                                    tags={this.props.project.tags}
                                    lockedTags={this.state.lockedTags}
                                    selectedRegions={this.state.selectedRegions}
                                    labels={labels}
                                    onChange={this.onTagsChanged}
                                    onLockedTagsChange={this.onLockedTagsChanged}
                                    onTagClick={this.onTagClicked}
                                    onCtrlTagClick={this.onCtrlTagClicked}
                                    onTagRename={this.confirmTagRename}
                                    onTagDeleted={this.confirmTagDeleted}
                                    onLabelEnter={this.onLabelEnter}
                                    onLabelLeave={this.onLabelLeave}
                                    onTagChanged={this.onTagChanged}
                                    ref={this.tagInputRef}
                                />
                            </div>
                            <Confirm title={strings.editorPage.tags.rename.title}
                                ref={this.renameTagConfirm}
                                message={strings.editorPage.tags.rename.confirmation}
                                confirmButtonTheme={getPrimaryRedTheme()}
                                onCancel={this.onTagRenameCanceled}
                                onConfirm={this.onTagRenamed} />
                            {/* <Confirm title="Save the changes"
                                ref={this.showConfirmPop}
                                message="If you leave changes will not be saved."
                                confirmButtonTheme={getPrimaryRedTheme()}
                                onCancel={this.onCanceingCOnfirmPopup}
                                onConfirm={this.onConfirmSaveTheChanges} /> */}
                            <Confirm title={`${strings.editorPage.tags.delete.title}`}
                                ref={this.deleteTagConfirm}
                                message={strings.editorPage.tags.delete.confirmation}
                                confirmButtonTheme={getPrimaryRedTheme()}
                                onConfirm={this.onTagDeletedConfirm} />
                            <Confirm title="Delete Value"
                                ref={this.deleteTagValueConfirm}
                                message={strings.editorPage.tags.delete.confirmation}
                                confirmButtonTheme={getPrimaryRedTheme()}
                                onConfirm={this.onTagValueDeleted} />
                            <Confirm title="Delete File"
                                ref={this.deleteFileConfirm}
                                message="Are you sure you want to delete this File ?"
                                confirmButtonTheme={getPrimaryRedTheme()}
                                onCancel={this.onDeleteFileCancel}
                                onConfirm={this.onDeleteFile} />
                             <Confirm title={`${strings.editorPage.tags.delete.title}/Tag Value`}
                                ref={this.deleteTagOrTagValueConfirm}
                                message=""
                                confirmButtonTheme={getPrimaryRedTheme()}
                                onTagValueDelete
                                confirmButtonTagText="Delete Tag Value"
                                confirmButtonText="Delete Tag"
                                onConfirm={this.onTagDeleted}
                                onTagValueConfirm={this.onDeleteTagValue} />
                        </div>
                    </SplitPane>
                    <Alert
                        show={this.state.showInvalidRegionWarning}
                        title={strings.editorPage.messages.enforceTaggedRegions.title}
                        // tslint:disable-next-line:max-line-length
                        message={strings.editorPage.messages.enforceTaggedRegions.description}
                        onClose={() => this.setState({ showInvalidRegionWarning: false })}
                    />
                    <Alert
                        show={this.state.isError}
                        title={this.state.errorTitle || "Error"}
                        message={this.state.errorMessage}
                        onClose={() => this.setState({
                            isError: false,
                            errorTitle: undefined,
                            errorMessage: undefined,
                        })}
                    />
                    <PreventLeaving
                        when={isRunningOCRs || isCanvasRunningOCR}
                        message={"An OCR operation is currently in progress, are you sure you want to leave?"}
                    />
                    <SkipButton skipTo="pageEditor">{strings.common.skipToMainContent}</SkipButton>
                    </BeforeUnloadComponent> 
                </div>
            </>
        );
    }

    /** render Label Data */
    private getLabelValue = (row) => {
        const {  selectedAsset } = this.state;
        const labelsData = selectedAsset && selectedAsset.labelData && selectedAsset.labelData.labels;
        const values = [];
        if (size(labelsData) > 0) {
        labelsData.map((data) => {
            if (data.label === row.name) {
                const text = data && data.value && data.value.map((text) => (text.text));
                values.push(text);
            }
        });
        if (size(values) > 0) {
        return values.map((text) => {
            return (
                <>
                {text}
                <a href="#/" title="delete" className="del-ico"> <i className="fa fa-trash-alt"></i> </a>
                </>
            );
            });
        } else {
            return (
            "Get Label"
            );
        }
    } else {
        return (
        "Get Label"
        );
    }
    }

    /**
     * get table rows
     */
    private getTableRows = (tableBindedRows) => {
        const obj = {};
        tableBindedRows.forEach((column: string) => {
            if (column) {
            const columnValue = column.replace( /\s/g, "").toLocaleLowerCase();
            obj[columnValue] = columnValue;
        }
        });
        return obj;
    }
    /**
     * add Row
     */
    private addRow = () => {
        const { tableData, tableBindedRows } = this.state;
        if (tableData.length > 0) {
            // const obj = {};
            // tableBindedRows.forEach((column: string) => {
            //     if (column) {
            //     const columnValue = column.replace( /\s/g, "").toLocaleLowerCase();
            //     obj[columnValue] = columnValue;
            // }
            // });
            const obj = this.getTableRows(tableBindedRows);
            tableData.push(obj);
        } else {
            alert("Please add some cols");
        }
        // columnNames.filter((column, index) => {
        // const name = `table1_r${index + 1}_c${index + 1}_${column.columnTagName}`;
        // tableRows.push(name);
        // });
        this.setState({ tableData });
    }

    /** Get Column Name */
    private getColumnName = (event) => {
            // validate and add
            if (event.target.value) {
            this.creatTableColumn(event.target.value);
            }
    }

    /** showTabe on  Page Load */
    private showTableOnPageLoad = () => {
        const { columnNames } = this.state;
        if (size(columnNames) > 0 ) {
        const rowVales = columnNames.filter((column) => column.tableFormat === true);
        this.setState({ columnNames: rowVales, tableRows: rowVales, showTableInput: false });
    }
    }

    private getMapSize = (data) => {
        return Object.getOwnPropertyNames(data).length;
    }

    /** Add column */
    private addColumn = () => {
        const { columnName, tableData } = this.state;
        // if (columnName.columnTagName) {
        // try {
        //     // this.validateTagLength(tag);
        //     this.validateTagUniqness(columnName, this.state.columnNames);
        // } catch (error) {
        //     toast.warn(error.toString());
        //     return;
        // }
        if (tableData.length === 0) {
            const ddd = {};
            ddd[`column${tableData.length}`] = columnName.columnTagName;
            tableData.push(ddd);
        } else {
            tableData.forEach((data, index) => {
                const length = this.getMapSize(data);
                tableData[index][`column${length}`] = columnName.columnTagName;
            });
        }
        const columnDataKeys = Object.keys(tableData[0]);
        const columnDataValues = Object.values(tableData[0]);
        if (tableData.length === 1) {
             tableData.push(this.getTableRows(columnDataKeys));
        }
        this.setState({ tableBindedColumns: columnDataValues,  tableBindedRows: columnDataKeys, tableData });

        // if (columnName.columnTagName) {
        // try {
        //     // this.validateTagLength(tag);
        //     this.validateTagUniqness(columnName, this.state.columnNames);
        // } catch (error) {
        //     toast.warn(error.toString());
        //     return;
        // }

        // const columnNames = [...this.state.columnNames, columnName];
        // const rowValue: ITag = {
        //         name: `table1_r1_c${columnNames.length}_${columnName.columnTagName}`,
        //         color: getNextColor(columnNames),
        //         columnTagName: `${columnName.columnTagName}`,
        //         type: FieldType.String,
        //         tableFormat: true,
        //         format: FieldFormat.NotSpecified,
        //         };
        // if (size(tableRows) === 0) {
        //     tableRows.push(rowValue);
        // }
        // tableRows.push(rowValue);
        // this.onTagsChanged(tableRows);
    //     this.setState({ columnNames });
    // }
    }

    /**
     * Get ITag Value
     */
    private getITagValue = (columnName, rowIndex, colIndex) => {
        const columnValue: ITag = {
                name: `table1_r${rowIndex}_c${colIndex + 1}_${columnName}`,
                color: getNextColor(this.state.tags),
                columnTagName: `${columnName}`,
                type: FieldType.String,
                tableFormat: true,
                format: FieldFormat.NotSpecified,
                };
        return columnValue;
    }

    /** get table Row */
    private saveSelectedTableValue = async (columnValue, index, colIndex) => {
        const ITagData = await this.getITagValue(this.state.tableData[0][columnValue], index, colIndex);
        const tags = [...this.state.tags, ITagData];
        const newValue = {
            ...ITagData,
        };
        await this.onTagsChanged(tags);
        await this.onTagChanged(ITagData, newValue);
        await this.setState({
            selectedTag: ITagData.name,
            lockedTags: [],
        }, () => this.canvas.current.applyTag(ITagData.name));
    }

    private onTableTagChanged = async (oldTag: ITableTag, newTag: ITableTag) => {
        const assetUpdates = await this.props.actions.updateProjectTableTag(this.props.project, oldTag, newTag);
        const selectedAsset = assetUpdates.find((am) => am.asset.id === this.state.selectedAsset.asset.id);

        if (selectedAsset) {
            this.setState({
                selectedAsset,
            });
        }
    }

    private creatTableColumn = (value: any) => {
        const newColumn: ITag = {
            name: null,
            color: "",
            columnTagName: value,
            type: FieldType.String,
            tableFormat: true,
            format: FieldFormat.NotSpecified,
        };
        if (newColumn.columnTagName.length
            && ![...this.state.columnNames, newColumn].containsDuplicates((t) => t.columnTagName)) {
            this.setState({ columnName: newColumn });
            // this.addColumn(newColumn);
        }
    }
    private validateTagUniqness = (newColumn: ITag, tableColumns: ITag[]) => {
        if (tableColumns.some((t) => isNameEqual(t.columnTagName, newColumn.columnTagName))) {
            throw new Error(strings.tags.warnings.existingName);
            // toast.warn(strings.tags.warnings.existingName);
        } else if (!newColumn.columnTagName.length) {
           toast.warn(strings.tags.warnings.emptyName);
        }
    }

    /** gettable Name */ // tableName
    private getTableName = (event) => {
        if (event.target.value) {
            this.setState({ tableName: event.target.value });
        }
    }
    /** Save Table name */
    private saveTableName = () => {
        const { tableName } = this.state;
        if (tableName) {
            this.setState({ showTableInput: false });
        }
    }

    /** delete file */
    private deleteAsset = (asset: IAsset) => {
        this.deleteFileConfirm.current.open(asset);
    }

    private onDeleteFileCancel = () => {
        if (this.renameCanceled) {
            this.renameCanceled();
            this.renameCanceled = null;
        }
    }

    private onDeleteFile = async (asset: IAsset) => {
        this.updateAssetState(asset.id, false, true);
        const { project } = this.props;
        const parts = asset.name.split("/");
        const fileName = parts.pop();
        const headers = accessToken();
        const URL = deleteFileEndPoint(project.name, "training", fileName);
        try {
            const deleted = await axios(URL, { method: "GET", headers });
            if (deleted && deleted.data === "Blobs deleted successfully!") {
            this.props.actions.deleteAsset(asset);
            this.loadProjectAssets();
            } else {
                this.loadProjectAssets();
            }
        } catch (err) {
            let response;
            if (err.response.status == 401) {
                const body = {
                    refreshToken: refreshTokenCall()
                }
                response = await ServiceHelper.postWithAutoRetry(
                    refreshTokenEndPoint(), body, { headers });
                if (response.data.status == 200) {
                    const signInUserSession = localStorage.getItem("signInUserSession");
                    const user = signInUserSession && JSON.parse(signInUserSession);
                    const updateAccessToken = Object.assign({}, user)
                    updateAccessToken.access_token = response.data.access_token
                    updateAccessToken.refresh_token = response.data.refresh_token
                    try {
                        const deleted = await axios(URL, { method: "GET", headers });
                        if (deleted && deleted.data === "Blobs deleted successfully!") {
                            this.props.actions.deleteAsset(asset);
                            this.loadProjectAssets();
                            } else {
                                this.loadProjectAssets();
                            }
                    } catch (err) {
                        ServiceHelper.handleServiceError(err);
                    }

                }
            } else {
                ServiceHelper.handleServiceError(err);
            }
        }
    }

    // call function from child
    private onPageContainerClick = () => {
        // workaround: tagInput will not lost focus with olmap,
        // so we fire the blur event manually here
        this.tagInputRef.current.triggerNewTagBlur();
    }

    // tslint:disable-next-line:no-empty
    private onPageClick = () => {
    }

    /**
     * Called when the asset side bar is resized
     * @param newWidth The new sidebar width
     */
    private onSideBarResize = (newWidth: number) => {
        this.setState({
            thumbnailSize: {
                width: newWidth,
                height: newWidth / (4 / 3),
            },
        });
    }

    /**
     * Called when the asset sidebar has been completed
     */
    private onSideBarResizeComplete = () => {
        const appSettings = {
            ...this.props.appSettings,
            thumbnailSize: this.state.thumbnailSize,
        };

        this.props.applicationActions.saveAppSettings(appSettings);
    }

    /**
     * Called when a tag from footer is clicked
     * @param tag Tag clicked
     */
    private onTagClicked = (tag: ITag): void => {
        this.setState({
            selectedTag: tag.name,
            lockedTags: [],
        }, () => this.canvas.current.applyTag(tag.name));
    }

    /**
     * Open confirm dialog for tag renaming
     */
    private confirmTagRename = (tag: ITag, newTag: ITag, cancelCallback: () => void): void => {
        this.renameCanceled = cancelCallback;
        this.renameTagConfirm.current.open(tag, newTag);
    }

    /**
     * Renames tag in assets and project, and saves files
     * @param tag Tag to be renamed
     * @param newTag Tag with the new name
     */
    private onTagRenamed = async (tag: ITag, newTag: ITag): Promise<void> => {
        this.renameCanceled = null;
        const assetUpdates = await this.props.actions.updateProjectTag(this.props.project, tag, newTag);
        const selectedAsset = assetUpdates.find((am) => am.asset.id === this.state.selectedAsset.asset.id);

        if (selectedAsset) {
            if (selectedAsset) {
                this.setState({ selectedAsset });
            }
        }
    }

    private onTagRenameCanceled = () => {
        if (this.renameCanceled) {
            this.renameCanceled();
            this.renameCanceled = null;
        }
    }

    private onCanceingCOnfirmPopup = () => {
        if (this.renameCanceled) {
            this.renameCanceled();
            this.renameCanceled = null;
        }
        this.setState({ showConfirmPopUpMessage: false });
    }

    private onConfirmSaveTheChanges = () => {
        this.props.history.goForward();
        this.setState({ showConfirmPopUpMessage: false });
         console.log(this.props.history, "*****on Saving");
    }

    /**
     * Open Confirm dialog for tag deletion
     */
    private confirmTagDeleted = (tagName: string): void => {
        this.deleteTagOrTagValueConfirm.current.open(tagName);
        // this.deleteTagConfirm.current.open(tagName);
    }

    private onTagValueDeleted = async (tagName: string): Promise<void> => {
        const assetUpdates = await this.props.actions.deleteTagFromFile(this.props.project, tagName,
             this.state.selectedAsset.asset);

        const selectedAsset = assetUpdates.find((am) => am.asset.id === this.state.selectedAsset.asset.id);
        if (selectedAsset) {
            this.setState({ selectedAsset });
        }
    }

    private onTagDeletedConfirm = async (tagName: string): Promise<void> => {
        const assetUpdates = await this.props.actions.deleteProjectTag(this.props.project, tagName);
        // const assetUpdates = await this.props.actions.deleteTagFromFile(this.props.project, tagName,
            //  this.state.selectedAsset.asset);

        const selectedAsset = assetUpdates.find((am) => am.asset.id === this.state.selectedAsset.asset.id);
        if (selectedAsset) {
            this.setState({ selectedAsset });
        }
    }

    private onDeleteTagValue = (tagName: string): void => {
        this.deleteTagValueConfirm.current.open(tagName);
    }

    /**
     * Removes tag from assets and projects and saves files
     * @param tagName Name of tag to be deleted
     */
    private onTagDeleted = async (tagName: string): Promise<void> => {
        this.deleteTagConfirm.current.open(tagName);
        // const assetUpdates = await this.props.actions.deleteProjectTag(this.props.project, tagName);
        // const assetUpdates = await this.props.actions.deleteTagFromFile(this.props.project, tagName,
        //      this.state.selectedAsset.asset);

        // const selectedAsset = assetUpdates.find((am) => am.asset.id === this.state.selectedAsset.asset.id);
        // console.log(selectedAsset, "---selectedAsset----");
        // if (selectedAsset) {
        //     this.setState({ selectedAsset });
        // }
    }

    private onCtrlTagClicked = (tag: ITag): void => {
        const locked = this.state.lockedTags;
        this.setState({
            selectedTag: tag.name,
            lockedTags: CanvasHelpers.toggleTag(locked, tag.name),
        }, () => this.canvas.current.applyTag(tag.name));
    }

    private getTagFromKeyboardEvent = (event: KeyboardEvent): ITag => {
        const index = tagIndexKeys.indexOf(event.key);
        const tags = this.props.project.tags;
        if (index >= 0 && index < tags.length) {
            return tags[index];
        }
        return null;
    }

    /**
     * Listens for {number key} and calls `onTagClicked` with tag corresponding to that number
     * @param event KeyDown event
     */
    private handleTagHotKey = (event: KeyboardEvent): void => {
        const tag = this.getTagFromKeyboardEvent(event);
        if (tag) {
            this.onTagClicked(tag);
        }
    }

    /**
     * Returns a value indicating whether the current asset is taggable
     */
    private isTaggableAssetType = (asset: IAsset): boolean => {
        return asset.type !== AssetType.Unknown;
    }

    /**
     * Raised when the selected asset has been changed.
     * This can either be a parent or child asset
     */
    private onAssetMetadataChanged = async (assetMetadata: IAssetMetadata): Promise<void> => {
        // Comment out below code as we allow regions without tags, it would make labeler's work easier.

        const initialState = assetMetadata.asset.state;

        const asset = { ...assetMetadata.asset };

        if (this.isTaggableAssetType(assetMetadata.asset)) {
            assetMetadata.asset.state = _.get(assetMetadata, "labelData.labels.length", 0) > 0 ?
                AssetState.Tagged :
                AssetState.Visited;
        } else if (assetMetadata.asset.state === AssetState.NotVisited) {
            assetMetadata.asset.state = AssetState.Visited;
        }

        // Only update asset metadata if state changes or is different
        if (initialState !== assetMetadata.asset.state || this.state.selectedAsset !== assetMetadata) {
            await this.props.actions.saveAssetMetadata(this.props.project, assetMetadata);
            if (this.props.project.lastVisitedAssetId === assetMetadata.asset.id) {
                this.setState({ selectedAsset: assetMetadata });
            }
        }

        // Find and update the root asset in the internal state
        // This forces the root assets that are displayed in the sidebar to
        // accurately show their correct state (not-visited, visited or tagged)
        const assets = [...this.state.assets];
        const assetIndex = assets.findIndex((a) => a.id === asset.id);
        if (assetIndex > -1) {
            assets[assetIndex] = {
                ...asset,
            };
        }

        this.setState({ assets, isValid: true });

        // Workaround for if component is unmounted
        if (!this.isUnmount) {
            this.props.appTitleActions.setTitle(`${this.props.project.name} - [ ${asset.name} ]`);
        }
    }

    /**
     * Raised when the asset binary has been painted onto the canvas tools rendering canvas
     */
    private onCanvasRendered = async (canvas: HTMLCanvasElement) => {
        // When active learning auto-detect is enabled
        // run predictions when asset changes
    }

    private onSelectedRegionsChanged = (selectedRegions: IRegion[]) => {
        this.setState({ selectedRegions });
    }

    private onTagsChanged = async (tags) => {
        const project = {
            ...this.props.project,
            tags,
        };
        await this.props.actions.saveProject(project, true, false);
    }

    private onLockedTagsChanged = (lockedTags: string[]) => {
        this.setState({ lockedTags });
    }

    private onBeforeAssetSelected = (): boolean => {
        if (!this.state.isValid) {
            this.setState({ showInvalidRegionWarning: true });
        }

        return this.state.isValid;
    }

    private selectAsset = async (asset: IAsset): Promise<void> => {
        // Nothing to do if we are already on the same asset.
        if (this.state.selectedAsset && this.state.selectedAsset.asset.id === asset.id) {
            return;
        }
        if (!this.state.isValid) {
            this.setState({ showInvalidRegionWarning: true });
            return;
        }
        if (asset.labelFilename) {
        await this.props.actions.updateProjectTagsFromExistingLabelFile(this.props.project, asset.labelFilename);
        }
        const assetMetadata = await this.props.actions.loadAssetMetadata(this.props.project, asset);
        try {
            if (!assetMetadata.asset.size) {
                const assetProps = await HtmlFileReader.readAssetAttributes(asset);
                assetMetadata.asset.size = { width: assetProps.width, height: assetProps.height };
            }
        } catch (err) {
            console.warn("Error computing asset size");
        }
        // selected Asset
        this.setState({
            selectedOcrFile: null,
            selectedAsset: assetMetadata,
        }, async () => {
            await this.getCanvasOcr(assetMetadata);
            await this.onAssetMetadataChanged(assetMetadata);
            await this.props.actions.saveProject(this.props.project, false, false);
        });
    }

    private getCanvasOcr = async (assetMetadata: IAssetMetadata) => {
        const parts = assetMetadata.asset.name.split("/");
        const fileName = parts.pop() || parts.pop();
        if (assetMetadata.asset.ocrContent) {
        const ocrContent = this.props.generatedOcrContentsData.find((ocrFile) => ocrFile.fileName === fileName);
        if (!ocrContent) {
        // this.updateAssetState(assetMetadata.asset.id, true);
        const parts = assetMetadata.asset.name.split("/");
        const fileName = parts.pop() || parts.pop();
        const fileObject = {
            modelName: this.props.project.name,
            file_name:  fileName,
            keyword: "training",
            asset: assetMetadata.asset,
        };
        // this.updateAssetState(assetMetadata.asset.id, true, false);
        const ocrContentIs = await this.props.actions.getOcrParticularFile(fileObject, this.props.project);
        if (this.props.project.lastVisitedAssetId === assetMetadata.asset.id) {
           this.setState({ selectedOcrFile: ocrContentIs, selectedAsset: assetMetadata });
        }
        } else {
            if (this.props.project.lastVisitedAssetId === assetMetadata.asset.id) {
                this.setState({ selectedOcrFile: ocrContent, selectedAsset: assetMetadata });
            }
        }
    } else  if (!assetMetadata.asset.ocrContent) {
        const ocrContent = this.props.generatedOcrContentsData.find((ocrFile) => ocrFile.fileName === fileName);
        if (!ocrContent) {
        this.updateAssetState(assetMetadata.asset.id, true, false);
        const ocrContentIs = await this.props.actions.generateOcrForSingleFile(this.props.project, fileName, assetMetadata.asset);
        this.updateAssetState(assetMetadata.asset.id, false, false);
        if (this.props.project.lastVisitedAssetId === assetMetadata.asset.id) {
            this.setState({ selectedOcrFile: ocrContentIs, selectedAsset: assetMetadata });
         }
        } else {
            if (this.props.project.lastVisitedAssetId === assetMetadata.asset.id) {
                this.setState({ selectedOcrFile: ocrContent, selectedAsset: assetMetadata });
            }
        }
    }
    }

    private loadProjectAssets = async (): Promise<void> => {
        if (this.loadingProjectAssets) {
            return;
        }
        this.loadingProjectAssets = true;
        const projectCopy = await Object.assign({}, this.props.project);
        projectCopy.folderPath = `${this.props.match.params["projectName"]}-${this.state.userId}/training`;
        await this.props.actions.saveProject(projectCopy, false, false);
        const rootAssets: IAsset[] = this.props.loadAnnotateFiles;
        if (this.state.assets.length === rootAssets.length
            && this.state.assets.map((asset) => asset.id).join(",") === rootAssets.map((asset) => asset.id).join(",")
            && this.state.assets.every((o, i) => Object.keys(o).length === Object.keys(rootAssets[i]).length && Object.keys(o).every(k => o[k] === rootAssets[i][k]))) {
            this.loadingProjectAssets = false;
            return;
        }
        const lastVisited = await rootAssets.length > 0 && rootAssets.find((asset) => asset.id === projectCopy.lastVisitedAssetId);
        if (this.props.project.fromModel) {
            localStorage.setItem("filesLengthFromModel", rootAssets.length.toString());
        }
        this.setState({
            assets: rootAssets,
        }, async () => {
            await this.props.actions.updateProjectTagsFromFiles(this.props.project);
            this.setState({ tagsLoaded: true });
            await this.props.actions.saveProject(this.props.project, false, false);
            if (rootAssets.length > 0 ) {
                await this.selectAsset(lastVisited ? lastVisited : rootAssets[0]);
            }
            this.loadingProjectAssets = false;
        });
    }

    private updateAssets = () => {
        const rootAssets: IAsset[] = this.props.loadAnnotateFiles;
        this.setState({
            assets: rootAssets,
        });
    }

    private loadAllOCRs = async () => {
        if (this.state.isRunningOCRs) {
            return;
        }

        const { project } = this.props;
        if (this.state.assets) {
            this.setState({ isRunningOCRs: true });
            try {
                await throttle(
                    constants.maxConcurrentServiceRequests,
                    this.state.assets.filter((asset) => asset.state === AssetState.NotVisited).map((asset) => asset.id),
                    async (assetId) => {
                        // Get the latest version of asset.
                        const asset = await this.state.assets.find((asset) => asset.id === assetId);
                        if (asset && asset.state === AssetState.NotVisited) {
                            try {
                                this.updateAssetState(asset.id, true, false);
                                const parts = asset.name.split("/");
                                const fileName = parts.pop() || parts.pop();
                                await this.props.actions.generateOcrForSingleFile(project, fileName, asset);
                                this.updateAssetState(asset.id, false, false);
                          } catch (err) {
                                this.updateAssetState(asset.id, false, false);
                                this.setState({
                                    isError: true,
                                    errorTitle: err.title,
                                    errorMessage: err.message,
                                });
                            }
                        }
                    });
            } finally {
                this.setState({ isRunningOCRs: false });
            }
        }
    }

    private updateAssetState = (id: string, isRunningOCR: boolean,  isDeleting?: boolean, assetState?: AssetState) => {
        // debugger;
        this.setState((state) => ({
            assets: state.assets.map((asset) => {
                if (asset.id === id) {
                    const updatedAsset = { ...asset, isRunningOCR, isDeleting };
                    if (assetState !== undefined && asset.state === AssetState.NotVisited) {
                        updatedAsset.state = assetState;
                    }
                    return updatedAsset;
                } else {
                    return asset;
                }
            }),
        }), () => {
            // debugger;
            if (this.state.selectedAsset && id === this.state.selectedAsset.asset.id) {
                const asset = this.state.assets.find((asset) => asset.id === id);
                if (asset) {
                    this.setState({
                        selectedAsset: { ...this.state.selectedAsset, asset: { ...asset } },
                    });
                }
            }
        });
    }

    /**
     * Updates the root asset list from the project assets
     */
    private updateRootAssets = () => {
        const updatedAssets = [...this.state.assets];
        updatedAssets.forEach((asset) => {
            const projectAsset = _.get(this.props, "project.assets[asset.id]", null);
            if (projectAsset) {
                asset.state = projectAsset.state;
            }
        });

        this.setState({ assets: updatedAssets, showConfirmPopUpMessage: true });
    }

    private onLabelEnter = (label: ILabel) => {
        this.setState({ hoveredLabel: label });
    }

    private onLabelLeave = (label: ILabel) => {
        this.setState({ hoveredLabel: null });
    }

    private onCanvasRunningOCRStatusChanged = (isCanvasRunningOCR: boolean) => {
        this.setState({ isCanvasRunningOCR });
    }

    private onFocused = async () => {
        await this.loadProjectAssets();
    }

    private onTagChanged = async (oldTag: ITag, newTag: ITag) => {
        const assetUpdates = await this.props.actions.updateProjectTag(this.props.project, oldTag, newTag, );
        const selectedAsset = assetUpdates.find((am) => am.asset.id === this.state.selectedAsset.asset.id);

        if (selectedAsset) {
            this.setState({
                selectedAsset,
            });
        }
    }
}
